import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../utility/apiFetch.js";

export function useStrainCatalogQuery() {
  return useQuery({
    queryKey: ["strain-catalog"],
    queryFn: () => apiFetch("strain-catalog"),
    staleTime: 300_000,
  });
}

export function useInventoryQuery() {
  return useQuery({
    queryKey: ["inventory"],
    queryFn: () => apiFetch("inventory"),
    staleTime: 60_000,
  });
}

export function useInventoryItemQuery(id) {
  return useQuery({
    queryKey: ["inventory", id],
    queryFn: () => apiFetch(`inventory/${id}`),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useInventoryLogQuery(id) {
  return useQuery({
    queryKey: ["inventory-log", id],
    queryFn: () => apiFetch(`inventory/${id}/log`),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useLowStockQuery() {
  return useQuery({
    queryKey: ["inventory-low-stock"],
    queryFn: () => apiFetch("inventory/low-stock"),
    staleTime: 60_000,
  });
}

export function useCreateInventoryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch("inventory", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["inventory"] }),
  });
}

export function useUpdateInventoryItem(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch(`inventory/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["inventory", id] });
    },
  });
}

export function useDeleteInventoryItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiFetch(`inventory/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["inventory"] }),
  });
}

export function useAdjustInventory(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch(`inventory/${id}/adjust`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["inventory", id] });
      qc.invalidateQueries({ queryKey: ["inventory-log", id] });
      qc.invalidateQueries({ queryKey: ["inventory-low-stock"] });
    },
  });
}

export function useIngredientsQuery(id) {
  return useQuery({
    queryKey: ["inventory-ingredients", id],
    queryFn: () => apiFetch(`inventory/${id}/ingredients`),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useAddIngredient(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch(`inventory/${id}/ingredients`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["inventory-ingredients", id] }),
  });
}

export function useRemoveIngredient(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ingredientId) =>
      apiFetch(`inventory/${id}/ingredients/${ingredientId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["inventory-ingredients", id] }),
  });
}

export function useManufactureItem(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      apiFetch(`inventory/${id}/manufacture`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["inventory", id] });
      qc.invalidateQueries({ queryKey: ["inventory-log", id] });
      qc.invalidateQueries({ queryKey: ["inventory-ingredients", id] });
    },
  });
}

export function useInventoryYieldStats(id) {
  return useQuery({
    queryKey: ["inventory-yield-stats", id],
    queryFn: () => apiFetch(`inventory/${id}/yield-stats`),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useReferralLinksQuery() {
  return useQuery({
    queryKey: ["referral-links"],
    queryFn: () => apiFetch("referral-links"),
    staleTime: 300_000,
  });
}

export function useInventoryLineageQuery(id) {
  return useQuery({
    queryKey: ["inventory", id, "lineage"],
    queryFn: () => apiFetch(`inventory/${id}/lineage`),
    enabled: !!id,
    staleTime: 60_000,
  });
}
